import React from 'react';
import { useFeaturedLanes } from '@/hooks/useFeaturedLanes';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FeaturedLanesProps {
  className?: string;
}

const FeaturedLanes: React.FC<FeaturedLanesProps> = ({ className }) => {
  const { lanes, isLoading } = useFeaturedLanes();

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        {[1, 2, 3].map(i => (
          <Card key={i} className="bg-card">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-32" />
            </CardHeader>
            <CardContent className="space-y-2">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (lanes.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
        ⭐ Featured This Week
      </h3>
      
      {lanes.map(lane => (
        <Card key={lane.id} className="bg-card border-border">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">{lane.emoji}</span>
              <span className="text-foreground">{lane.name}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {lane.athletes.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                No {lane.emoji} reactions yet this week. Be the first!
              </p>
            ) : (
              <div className="space-y-2">
                {lane.athletes.map((athlete, index) => (
                  <Link
                    key={athlete.id}
                    to={`/profile/${athlete.profile?.username || athlete.profile_id}`}
                    className="flex items-center gap-2 p-1.5 -mx-1.5 rounded-md hover:bg-accent transition-colors group"
                  >
                    <span className="text-xs font-bold text-muted-foreground w-4">
                      #{index + 1}
                    </span>
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={athlete.profile?.avatar_url || undefined} />
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {athlete.profile?.name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {athlete.profile?.name || 'Anonymous'}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-primary">
                      {athlete.score} {lane.emoji}
                    </span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default FeaturedLanes;
